/* ============================================================
   Feature Accordion — assets/feature-accordion.js
   Handles: accordion expand/collapse + center image carousel
   ============================================================ */

class FeatureAccordion {
  constructor(el) {
    this.el            = el;
    this.items         = Array.from(el.querySelectorAll('.m-fac__item'));
    this.behavior      = el.dataset.behavior || 'single'; // 'single' | 'multiple'

    // Carousel
    this.slides        = Array.from(el.querySelectorAll('.m-fac__slide'));
    this.prevBtn       = el.querySelector('.m-fac__carousel-prev');
    this.nextBtn       = el.querySelector('.m-fac__carousel-next');
    this.current       = 0;
    this.autoplay      = el.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(el.dataset.autoplaySpeed, 10) || 4000;
    this._autoplayTimer = null;

    el._featureAccordion = this;

    this._initAccordion();
    this._initCarousel();
  }

  // ── Accordion ────────────────────────────────────────────────

  _initAccordion() {
    this.items.forEach(item => {
      const header = item.querySelector('.m-fac__item-header');
      const body   = item.querySelector('.m-fac__item-body');

      // Set correct max-height for items that start open (set in Liquid via inline style)
      if (item.classList.contains('is-open') && body) {
        // Replace the rough inline value with the exact scrollHeight
        requestAnimationFrame(() => {
          body.style.maxHeight = body.scrollHeight + 'px';
        });
      }

      if (!header) return;
      header.addEventListener('click', () => this._toggle(item));
    });
  }

  _toggle(item) {
    const isOpen = item.classList.contains('is-open');
    const body   = item.querySelector('.m-fac__item-body');

    // In single mode: close every other item first
    if (this.behavior === 'single' && !isOpen) {
      this.items.forEach(other => {
        if (other === item) return;
        other.classList.remove('is-open');
        const otherBody = other.querySelector('.m-fac__item-body');
        if (otherBody) otherBody.style.maxHeight = '0';
      });
    }

    if (isOpen) {
      // Collapse
      item.classList.remove('is-open');
      if (body) body.style.maxHeight = '0';
    } else {
      // Expand
      item.classList.add('is-open');
      if (body) body.style.maxHeight = body.scrollHeight + 'px';
    }
  }

  // Public: open a specific item by index
  openItem(index) {
    const item = this.items[index];
    if (!item) return;
    if (!item.classList.contains('is-open')) this._toggle(item);
  }

  // ── Carousel ─────────────────────────────────────────────────

  _initCarousel() {
    if (this.slides.length <= 1) return;

    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this._prev(true));
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this._next(true));

    // Touch/swipe
    let startX = 0, startY = 0;
    const track = this.el.querySelector('.m-fac__carousel-track');
    if (track) {
      track.addEventListener('touchstart', e => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      }, { passive: true });
      track.addEventListener('touchend', e => {
        const dx = startX - e.changedTouches[0].clientX;
        const dy = Math.abs(startY - e.changedTouches[0].clientY);
        if (Math.abs(dx) > 50 && dy < 40) dx > 0 ? this._next(true) : this._prev(true);
      }, { passive: true });
    }

    if (this.autoplay) this._startAutoplay();
  }

  _startAutoplay() {
    this._stopAutoplay();
    this._autoplayTimer = setInterval(() => this._next(), this.autoplaySpeed);
  }

  _stopAutoplay() {
    if (this._autoplayTimer) {
      clearInterval(this._autoplayTimer);
      this._autoplayTimer = null;
    }
  }

  _next(manual = false) {
    this.current = (this.current + 1) % this.slides.length;
    this._updateCarousel();
    if (manual && this.autoplay) this._startAutoplay();
  }

  _prev(manual = false) {
    this.current = (this.current - 1 + this.slides.length) % this.slides.length;
    this._updateCarousel();
    if (manual && this.autoplay) this._startAutoplay();
  }

  _updateCarousel() {
    this.slides.forEach((s, i) => s.classList.toggle('is-active', i === this.current));
  }
}

// ── Boot ──────────────────────────────────────────────────────

function initFeatureAccordions(root) {
  (root || document).querySelectorAll('.m-feature-accordion').forEach(el => {
    if (!el._featureAccordion) new FeatureAccordion(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initFeatureAccordions());
} else {
  initFeatureAccordions();
}

// ── Shopify design-mode ───────────────────────────────────────

if (window.Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', e => {
    initFeatureAccordions(e.target);
  });

  document.addEventListener('shopify:section:unload', e => {
    const el = e.target.querySelector('.m-feature-accordion');
    if (el && el._featureAccordion) {
      el._featureAccordion._stopAutoplay();
      el._featureAccordion = null;
    }
  });

  // Selecting an accordion block in the editor opens it
  document.addEventListener('shopify:block:select', e => {
    const section = e.target.closest('.m-feature-accordion');
    if (!section || !section._featureAccordion) return;
    const instance = section._featureAccordion;

    if (e.target.classList.contains('m-fac__item')) {
      if (!e.target.classList.contains('is-open')) instance._toggle(e.target);
    }

    // Selecting a carousel image block shows that slide
    if (e.target.classList.contains('m-fac__slide')) {
      const idx = instance.slides.indexOf(e.target);
      if (idx >= 0) { instance.current = idx; instance._updateCarousel(); }
    }
  });
}
